import React from 'react';
import clsx from 'clsx';
import type { Movimentacao } from '../../../models';
import { mesclarEquipamentosMovimentacao } from '../../../models';
import { TIPO_EQUIPAMENTO_LABELS, TIPO_FLUXO_LABELS } from '../solicitacaoDetailsPage.definitions';
import { etapasDaMovimentacao } from '../etapasMovimentacao';
import { pendenciaMovimentacao } from '../pendenciaMovimentacao';
import { paraDataFormatada } from '../../SolicitacoesListPage/components/solicitacoesKanban.utils';

interface MovimentacoesResumoListaProps {
  movimentacoes: Movimentacao[];
  selecionadaId: string | null;
  onSelecionar: (id: string) => void;
  /** Só o SI vê o próximo passo de cada uma (mesma regra do card). */
  mostrarPendencia: boolean;
  /** Botão "+ Nova Movimentação" (só quando dá pra criar). */
  onNova?: () => void;
}

function resumoEquipamentos(movimentacao: Movimentacao): string {
  const itens = mesclarEquipamentosMovimentacao(movimentacao.equipamentosIntake ?? [], movimentacao.equipamentosExecucao ?? []);
  if (itens.length === 0) return 'nenhum equipamento ainda';
  return itens
    .map((item) => {
      const tipo = item.tipoEquipamento ? TIPO_EQUIPAMENTO_LABELS[item.tipoEquipamento] : 'a definir';
      return item.patrimonio ? `${tipo} ${item.patrimonio}` : tipo;
    })
    .join(' · ');
}

/**
 * Lateral do detalhe da Solicitação: um cartão por Movimentação com o que é (fluxo +
 * equipamentos), a barra das 6 etapas e o próximo passo. Clicar mostra a Movimentação grande
 * ao lado — com várias Movimentações, a tela não empilha tudo aberto de uma vez.
 */
export default function MovimentacoesResumoLista({
  movimentacoes,
  selecionadaId,
  onSelecionar,
  mostrarPendencia,
  onNova,
}: MovimentacoesResumoListaProps) {
  return (
    <nav aria-label="Movimentações" className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Movimentações</p>
      {movimentacoes.map((movimentacao) => {
        const etapas = etapasDaMovimentacao(movimentacao);
        const feitas = etapas.filter((etapa) => etapa.concluida).length;
        const pendencia = mostrarPendencia ? pendenciaMovimentacao(movimentacao) : null;
        const selecionada = movimentacao.id === selecionadaId;
        const cancelada = movimentacao.statusMovimentacao === 'cancelada';
        return (
          <button
            key={movimentacao.id}
            type="button"
            aria-current={selecionada ? 'true' : undefined}
            onClick={() => onSelecionar(movimentacao.id)}
            className={clsx(
              'w-full space-y-2 rounded-lg px-3 py-3 text-left transition-colors',
              selecionada ? 'border-2 border-primary-500 bg-primary-50' : 'border border-gray-200 bg-white hover:bg-gray-50',
              cancelada && 'opacity-60'
            )}
          >
            <span className="flex items-center justify-between gap-2">
              <span className="font-semibold text-gray-900">{TIPO_FLUXO_LABELS[movimentacao.tipoFluxo]}</span>
              {cancelada ? (
                <span className="text-xs text-gray-500">Cancelada</span>
              ) : (
                <span className="text-xs text-gray-500">{paraDataFormatada(movimentacao.dataPrevista) ?? 'sem data'}</span>
              )}
            </span>
            <span className="block truncate text-xs text-gray-600">{resumoEquipamentos(movimentacao)}</span>
            {!cancelada && (
              <span className="flex gap-0.5" role="img" aria-label={`${feitas} de ${etapas.length} etapas concluídas`}>
                {etapas.map((etapa) => (
                  <span
                    key={etapa.id}
                    title={`${etapa.label}${etapa.concluida ? ' — concluída' : ''}`}
                    className={clsx('h-1 flex-1 rounded-full', etapa.concluida ? 'bg-primary-500' : 'bg-gray-200')}
                  />
                ))}
              </span>
            )}
            {pendencia && <span className="block text-xs font-medium text-amber-800">Próximo: {pendencia.label}</span>}
          </button>
        );
      })}
      {onNova && (
        <button
          type="button"
          onClick={onNova}
          className="w-full rounded-lg border border-dashed border-primary-300 bg-white px-3 py-2.5 text-sm font-semibold text-primary-700 hover:bg-primary-50"
        >
          + Nova Movimentação
        </button>
      )}
    </nav>
  );
}
